import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { getStoredAuth } from "@/lib/api";
import { useState } from "react";
import { useAuth, type AdminRole, type Permission, ROLE_LABEL } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Building2, Calendar, Users2, BarChart4, ArrowLeft, Lock,
  Shield, Hotel, PartyPopper, TreePine, Mountain, Gavel, Crown, UserCog
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/roles")({
  beforeLoad: ({ location }) => {
    if (typeof window === "undefined") return;
    const auth = getStoredAuth();
    if (!auth) throw redirect({ to: "/login", search: { redirect: location.pathname } });
    if (auth.role !== "super_admin") throw redirect({ to: "/admin/dashboard" });
  },
  component: RolesPage,
});

type GroupedPerms = {
  category: string;
  icon: React.ComponentType<{ className?: string }>;
  perms: { key: Permission; label: string }[];
};

const PERM_GROUPS: GroupedPerms[] = [
  {
    category: "Venues",
    icon: Building2,
    perms: [
      { key: "venues.manage_all", label: "Manage all listings" },
      { key: "venues.manage_assigned", label: "Manage owned listings" },
      { key: "venues.moderate", label: "Moderate listings" },
      { key: "venues.approve", label: "Approve listings" },
      { key: "venues.feature", label: "Feature venues" },
    ],
  },
  {
    category: "Bookings",
    icon: Calendar,
    perms: [
      { key: "bookings.manage", label: "Manage bookings" },
      { key: "bookings.view", label: "View bookings" },
    ],
  },
  {
    category: "Users",
    icon: Users2,
    perms: [
      { key: "users.manage", label: "Manage users" },
      { key: "users.view", label: "View users" },
      { key: "admins.manage", label: "Manage admin roles" },
    ],
  },
  {
    category: "Platform",
    icon: BarChart4,
    perms: [
      { key: "settings.manage", label: "System settings" },
      { key: "reports.access", label: "Reports & invoices" },
    ],
  },
];

const ROLE_META: Record<AdminRole, { desc: string; icon: React.ComponentType<{ className?: string }>; color: string }> = {
  super_admin:        { desc: "Full system access. Can manage everything including roles, settings, and analytics.", icon: Crown, color: "text-amber-600 bg-amber-50 border-amber-200" },
  venue_manager:      { desc: "Manages assigned venue listings and daily bookings.", icon: UserCog, color: "text-blue-600 bg-blue-50 border-blue-200" },
  hotel_owner:        { desc: "Manages hotel rooms, guest bookings, and amenities.", icon: Hotel, color: "text-primary bg-primary/5 border-primary/20" },
  event_centre_owner: { desc: "Manages event halls, weddings, and conference bookings.", icon: PartyPopper, color: "text-violet-600 bg-violet-50 border-violet-200" },
  open_space_owner:   { desc: "Lists and manages parks, gardens, and open-air venues.", icon: TreePine, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  resort_owner:       { desc: "Manages luxury resort stays and vacation packages.", icon: Mountain, color: "text-amber-600 bg-amber-50 border-amber-200" },
  hotel_event_owner:    { desc: "Runs a hotel with event facilities. Dual management access.", icon: Shield, color: "text-rose-600 bg-rose-50 border-rose-200" },
  apartment_owner:      { desc: "Manages apartment units and short or long-term rentals.", icon: Building2, color: "text-sky-600 bg-sky-50 border-sky-200" },
  conference_hall_owner:{ desc: "Manages conference rooms, boardrooms, and seminar spaces.", icon: Users2, color: "text-rose-600 bg-rose-50 border-rose-200" },
  moderator:            { desc: "Reviews and approves property listings before publication.", icon: Gavel, color: "text-slate-600 bg-slate-50 border-slate-200" },
};

const ALL_ROLES: AdminRole[] = [
  "super_admin", "venue_manager", "hotel_owner", "event_centre_owner",
  "open_space_owner", "resort_owner", "hotel_event_owner",
  "apartment_owner", "conference_hall_owner", "moderator",
];

function RolesPage() {
  const { role: currentUserRole, rolePerms, updateRolePermissions } = useAuth();
  const [selected, setSelected] = useState<AdminRole>("super_admin");
  const [saving, setSaving] = useState<Set<string>>(new Set());

  const totalPerms = PERM_GROUPS.reduce((a, g) => a + g.perms.length, 0);

  const toggle = async (role: AdminRole, perm: Permission, on: boolean) => {
    if (role === "super_admin" && perm === "admins.manage") {
      toast.error("Cannot remove 'Manage admin roles' from Super Admin.");
      return;
    }
    const key = `${role}:${perm}`;
    if (saving.has(key)) return;

    setSaving(prev => new Set(prev).add(key));
    const current = rolePerms[role] || [];
    const next = on ? [...current, perm] : current.filter(p => p !== perm);

    try {
      await updateRolePermissions(role, next);
      const label = PERM_GROUPS.flatMap(g => g.perms).find(p => p.key === perm)?.label || perm;
      toast.success(`${label} ${on ? "enabled" : "disabled"} for ${ROLE_LABEL[role]}.`);
    } catch {
      toast.error("Failed to save change. It has been reverted.");
    } finally {
      setSaving(prev => {
        const nextSet = new Set(prev);
        nextSet.delete(key);
        return nextSet;
      });
    }
  };

  const activeRole = selected;
  const activeCount = rolePerms[activeRole]?.length || 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Link to="/admin/dashboard" className="flex items-center gap-1 hover:text-primary transition-colors">
          <ArrowLeft className="size-3" /> Dashboard
        </Link>
        <span>/</span>
        <span className="text-foreground">Roles</span>
      </div>

      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Roles & Permissions</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage what each role can access across the platform.</p>
      </div>

      {/* Layout: sidebar + panel */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        {/* Role sidebar */}
        <div className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-1">Select a role</h2>
          <div className="space-y-1">
            {ALL_ROLES.map(role => {
              const meta = ROLE_META[role];
              const Icon = meta.icon;
              const count = rolePerms[role]?.length || 0;
              const isActive = selected === role;
              const isYou = currentUserRole === role;
              return (
                <button
                  key={role}
                  onClick={() => setSelected(role)}
                  className={`w-full flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition-all ${
                    isActive
                      ? "border-primary/30 bg-primary/5 shadow-sm"
                      : "border-transparent hover:bg-accent/50"
                  }`}
                >
                  <div className={`grid size-9 place-items-center rounded-lg border ${meta.color}`}>
                    <Icon className="size-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold truncate">{ROLE_LABEL[role]}</span>
                      {isYou && (
                        <Badge className="text-[10px] py-0 px-1.5 bg-primary/10 text-primary border-0 font-semibold">You</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{count} / {totalPerms} perms</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Permission panel */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-soft overflow-hidden">
          {/* Panel header */}
          <div className="border-b border-border/60 px-6 py-5 flex items-start gap-4">
            <div className={`grid size-12 place-items-center rounded-xl border ${ROLE_META[activeRole].color}`}>
              {(() => {
                const Icon = ROLE_META[activeRole].icon;
                return <Icon className="size-6" />;
              })()}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="font-display text-xl font-bold">{ROLE_LABEL[activeRole]}</h2>
                <Badge variant="outline" className="text-xs font-semibold rounded-full">
                  {activeCount} / {totalPerms} active
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">{ROLE_META[activeRole].desc}</p>
            </div>
          </div>

          {/* Permissions */}
          <div className="p-6 space-y-8">
            {PERM_GROUPS.map(group => {
              const Icon = group.icon;
              return (
                <div key={group.category}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="size-4 text-muted-foreground" />
                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">{group.category}</h3>
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {group.perms.map(perm => {
                      const enabled = (rolePerms[activeRole] || []).includes(perm.key);
                      const locked = activeRole === "super_admin" && perm.key === "admins.manage";
                      const isSaving = saving.has(`${activeRole}:${perm.key}`);
                      return (
                        <div
                          key={perm.key}
                          className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors ${
                            enabled
                              ? "border-primary/20 bg-primary/[0.03]"
                              : "border-border/40 bg-background"
                          }`}
                        >
                          <span className={`text-sm font-medium ${enabled ? "text-foreground" : "text-muted-foreground"}`}>
                            {perm.label}
                          </span>
                          <div className="flex items-center gap-2 shrink-0">
                            {locked && <Lock className="size-3 text-muted-foreground" />}
                            <Switch
                              checked={enabled}
                              disabled={locked || isSaving}
                              onCheckedChange={c => toggle(activeRole, perm.key, c)}
                              aria-label={perm.label}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Panel footer */}
          <div className="border-t border-border/60 px-6 py-3 flex justify-between items-center text-xs text-muted-foreground bg-muted/20">
            <span>Changes persist to backend.</span>
            <Link to="/login" className="font-semibold text-primary hover:underline">Test as another role →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
